// Forms DSL — in-app documentation content for the workbench Docs modal.
//
// Each TOPIC is { id, group, title, keywords, body }. `body` is an array of
// blocks rendered by FormsDslDocs.jsx:
//   - a string            → a paragraph. Inline `code` is wrapped in backticks.
//                           A leading "## " makes a sub-heading; "- " a bullet.
//   - { code: `…` }       → a copy-able code block (DSL/SQL).
//
// Authored to be exhaustive: the grammar (FORMS_DSL_GRAMMAR.md), every widget +
// property, the binding/reactivity model, expressions, run steps, grids,
// validation, routing, a property reference, the error codes, and full worked
// recipes. Search matches title + keywords + all prose + all code.

export const DOC_GROUPS = [
  'Getting started',
  'Structure',
  'Widgets',
  'Binding & reactivity',
  'Expressions',
  'Buttons & actions',
  'Grids',
  'Validation',
  'Routing & targets',
  'Reference',
  'Recipes',
];

export const TOPICS = [
  // ── Getting started ────────────────────────────────────────────────────────
  {
    id: 'overview',
    group: 'Getting started',
    title: 'What the workbench does',
    keywords: 'analyze preview connection database save run workbench admin intro overview',
    body: [
      'The Forms DSL lets you describe a small data-entry form as a script. The workbench is where you write it, **Analyze** it into a plan, and see a **live preview** that actually runs its queries and actions against a connection.',
      '## The loop',
      '- Write the script in the editor (left).',
      '- Pick a **connection** (and optionally a **database**) in the toolbar — the preview needs one to run queries.',
      '- Click **▶ Analyze**. The script is parsed and validated; any problems show as diagnostics, otherwise the form renders on the right.',
      '- Interact with the preview to test it, then **💾 Save** it with a name so it appears in the Forms tab for end-users to run.',
      '## Two ways a form runs',
      'In the **workbench preview** (here), you are an admin and the script runs against the connection you pick. When an end-user runs a **saved** form from the Forms tab, the server drives the stored script and only its whitelisted SQL — the client never sends raw SQL. Design the same way for both; the engine handles the difference.',
      'Tip: use the **＋ Snippet** menu to drop ready-made building blocks at the cursor, and this **📖 Docs** dialog for the full reference and recipes.',
    ],
  },
  {
    id: 'first-form',
    group: 'Getting started',
    title: 'Your first form',
    keywords: 'hello world example beginner start simple first',
    body: [
      'A form is a list of statements: optional `meta`, then widgets, layout, buttons. Here is a complete, runnable one — a table you can search and a label that echoes the picked row:',
      { code: `meta { title = "Browse customers" width = "md" }

text q { label = "Search" }

table cust {
  label = "Customer"
  query = "SELECT KodeCust, NamaCust, Kota FROM m_customer WHERE search(:q, NamaCust, Kota) ORDER BY NamaCust"
  value = KodeCust
  show  = [KodeCust, NamaCust, Kota]
}

label picked { label = "You picked: {cust}" }` },
      'Paste it, set the query to a table that exists on your connection, click **Analyze**, then type in the search box and click a row. The `{cust}` in the label updates to the picked row’s value column (`KodeCust`). That reactivity — type → re-query → pick → labels/buttons update — is the whole model.',
    ],
  },
  {
    id: 'sigils',
    group: 'Getting started',
    title: 'Sigils cheat sheet (: vs {})',
    keywords: 'sigil colon bind brace interp interpolation cheat sheet sql display difference',
    body: [
      'Two sigils, two contexts. Mixing them up is the most common mistake, so the validator enforces the split:',
      '## `:name` — a value INTO SQL',
      'Use `:name` only inside **SQL strings**: `query`, `call`, `exec`, `insert`/`update`/`delete`, and a `require`’s SQL. Each `:name` is bound as a parameter (never pasted as text), so it is injection-safe.',
      { code: `query = "SELECT * FROM sales WHERE NoNota = :nota AND cabang = :cabang"` },
      '## `{name}` — a value INTO a display string',
      'Use `{name}` only inside **display strings**: `label`, `confirm`, `toast`, an `else` message, `placeholder`. It substitutes the value’s text.',
      { code: `confirm = "Delete nota {nota}?"
toast   = "Saved {nota} for {cabang}."` },
      'Cross-using them is an error (`E_SIGIL_CTX`): no `{}` in SQL, no `:` in a display string.',
      '## What a name resolves to',
      'A bare name in `{}` or `:` gives the binding’s **value column** (a scalar). In an **expression** (`enabled`, `visible`, `with{}`, `computed`) a bare table/select name is its **row object**, so you write `cust.NamaCust`. Mnemonic: *`{}` and `:` give the value column; an expression gives the row — add `.Column`.*',
      '## Grid rows — use `:row.<col>`, never a bare `:grid`',
      'A `grid` has **no single value**, so a bare `:items` / `{items}` (or `:rows`) means nothing — that’s a common mistake. How you reach a grid’s rows depends on *where you are*:',
      '- **Inside that grid’s own `insert` / `update` / `delete` + `before`/`after` hooks:** `:row.<col>` is the cell of the row being persisted — e.g. `:row.id`, `:row.NoNota`. On an `update`, `:row.old.<col>` is the value before the edit.',
      '- **From outside the grid** (a button `run`, a `let`, an expression): use the grid accessors `items.rows` / `items.added` / `items.edited` / `items.removed`, then **iterate** them — `for r in items.removed { … :r.<col> … }` — or aggregate, `sum(items.rows.total)`.',
      { code: `grid items {
  query  = "SELECT id, qty FROM tr_jual_d WHERE NoNota = :master ORDER BY id"
  key    = id
  delete = "sp_del(:master, :row.id)"   # ✅ inside the grid → the row's own cell
}

# ✅ outside the grid: loop its rows / changeset
button "Hapus terpilih" {
  run { for r in items.removed { call "sp_del(:master, :r.id)" } }
}

# ❌ wrong — these do NOT reference a row:
#   delete = "sp_del(:items)"     :items is the whole grid, not a row
#   delete = "sp_del(:rows.id)"   there is no \`rows\` binding` },
      'So your example is `:row.id` (the grid’s own delete/update) — not `:rows.id` and not a bare grid name.',
    ],
  },

  // ── Structure ──────────────────────────────────────────────────────────────
  {
    id: 'meta',
    group: 'Structure',
    title: 'meta — form header',
    keywords: 'meta title width connection database locale header config',
    body: [
      'Optional form-level configuration. All keys are optional.',
      { code: `meta {
  title      = "Judul Form"
  width      = "md"          # sm | md | lg | xl | full, or a raw size like "640px"
  connection = "Production"  # pin a connection by name (omit to use a picker/target)
  database   = "sales"       # pin a database
  locale     = "id-ID"       # number/date formatting locale
}` },
      '`width` sizes the whole run dialog: `sm` ≈ 460px, `md` ≈ 680px, `lg` ≈ 920px, `xl` ≈ 1160px, `full` = no cap; or pass any CSS size (`"640px"`).',
      'Pinning `connection`/`database` fixes where the form runs. Omit them to let the user choose with a `connection`/`database` widget or a `target` branch picker.',
    ],
  },
  {
    id: 'containers',
    group: 'Structure',
    title: 'Containers — section & row',
    keywords: 'section row layout group fieldset side by side container',
    body: [
      '`section "Title" { … }` draws a titled box; `row { … }` lays its children side-by-side. Nest freely.',
      { code: `section "Pelanggan" {
  row {
    select cust { label = "Customer"; query = "SELECT KodeCust, NamaCust FROM m_customer"; value = KodeCust; search = true }
    date   tgl  { label = "Tanggal"; default = today() }
  }
  text alamat { label = "Alamat"; default = cust.Alamat; visible = cust is set }
}` },
      'Containers are purely visual — they don’t change binding scope. A widget’s name is global to the form regardless of nesting.',
    ],
  },
  {
    id: 'spacer',
    group: 'Structure',
    title: 'spacer — vertical / horizontal gap',
    keywords: 'spacer gap space vertical horizontal layout margin padding breathing room direction size',
    body: [
      'A pure layout primitive — a fixed-size gap, with nothing to bind or reference (no name). `direction` is `vertical` (default) or `horizontal`; `size` is any CSS length (default `1rem`).',
      { code: `text nama { label = "Nama" }
spacer { size = "1.5rem" }         # extra vertical gap (on top of the form's own spacing)
text alamat { label = "Alamat" }

row {
  text kota    { label = "Kota" }
  spacer { direction = "horizontal"; size = "2rem" }   # fixed-width gap between two fields
  text kodepos { label = "Kode Pos" }
}` },
      'A vertical `spacer` is meant as a direct child of the form or a `section` (stacked layout); a horizontal one inside a `row` (side-by-side layout) — using the wrong direction in the wrong place just renders an inert empty box, it won’t error.',
    ],
  },
  {
    id: 'param',
    group: 'Structure',
    title: 'param — opening context',
    keywords: 'param parameter open with navigation required default input',
    body: [
      'A `param` is a value the form is **opened with** — typically passed by another form’s `open "X" with { … }`. It behaves like a read-only binding you can reference in queries and expressions.',
      { code: `param noJual { type = text; required = true }   # type: text | number | date
param mode   { type = text; default = "edit" }` },
      'A `required` param with no value supplied at open is a runtime error (`E_PARAM_REQUIRED`). See **Open another form** for the calling side.',
    ],
  },

  // ── Widgets ────────────────────────────────────────────────────────────────
  {
    id: 'inputs',
    group: 'Widgets',
    title: 'text / number / date / check',
    keywords: 'text number date check input field scalar readonly default min max pattern maxlen',
    body: [
      'The scalar inputs. Their value is a primitive (string / number / boolean).',
      { code: `text   nama   { label = "Nama" }
number qty    { label = "Qty"; type = number; min = 0; max = 9999 }
date   tgl    { label = "Tanggal"; default = today() }
check  lunas  { label = "Lunas" }
text   nomor  { label = "No"; default = sql.scalar("SELECT next_no()"); readonly = true }` },
      '## Common properties',
      '- `label` — the field caption (defaults to the name).',
      '- `default` — initial value: a literal, `today()` / `now()`, another binding, or `sql.scalar("…")` (evaluated once).',
      '- `readonly` — display/seed but don’t let the user edit (a read-only field with a `format` shows the formatted value).',
      '- `visible` / `enabled` — reactive booleans (see Reactivity).',
      '- `required`, `min` / `max` (number), `maxlen` / `pattern` (text) — enforced before a mutating action (and shown as live input hints). These checks are client-side; for a server-authoritative rule also add a `validate { }` SQL `require` or check in the SP.',
    ],
  },
  {
    id: 'select',
    group: 'Widgets',
    title: 'select — query or static options',
    keywords: 'select dropdown options query value display search enum combo picker',
    body: [
      'A dropdown. Its value is the **chosen row** (in expressions) or its value column (in `:`/`{}`). Supply exactly one of `query` or `options`.',
      '## From a query',
      { code: `select item {
  label   = "Item"
  query   = "SELECT id, name FROM my_table ORDER BY name"
  value   = id          # column that becomes the value of \`item\`
  display = name         # column shown in the dropdown (default: value/first col)
  search  = true         # false (default) | true (all cols) | [name, code]
}` },
      '## From a static enum',
      { code: `select status {
  label   = "Status"
  options = [{ value = "A", label = "Aktif" }, { value = "N", label = "Nonaktif" }]   # or just ["pcs", "box"]
  default = "A"
}` },
      'A `select` needs exactly one of `options` or `query` (`E_ENUM_SOURCE` otherwise).',
    ],
  },
  {
    id: 'table',
    group: 'Widgets',
    title: 'table — list + selection',
    keywords: 'table list grid rows select row show value search highlight selection align format column',
    body: [
      'Renders query rows as a table; clicking a row selects it (highlighted). The selected row is the widget’s value.',
      { code: `text q { label = "Cari" }
table results {
  label = "Hasil"
  query = "SELECT id, name, status FROM my_table WHERE search(:q, name, status) ORDER BY name"
  value = id              # the bound value (what :results / {results} resolve to)
  show  = [name, status]  # columns to display (default: all query columns)
}` },
      '`show` picks/orders the visible columns (default: every query column). `value` is the column used for the binding + selection match — `:results` / `{results}` resolve to it, and the clicked row is highlighted by matching it. Add `search = true` (filter all shown columns) or `search = [cols]` to render a built-in filter box, or the block form for options:',
      { code: `search = { columns = [nama, kode]; mode = "contains"; min = 2 }   # mode: contains | prefix | exact` },
      '`mode`: `contains` (default, `%term%`), `prefix` (`term%`), or `exact`. `min` is the minimum characters before filtering kicks in. The built-in box (`search = …` on the widget) is different from the SQL `search(:q, …)` builtin — the box filters this widget’s own list; `search(:q, …)` wires an external `text` field into the query. Use one or the other.',
      '## Column alignment + formatting',
      'Add an optional `column X { }` block per column for display styling — the same `align` / `format` a grid column has. `show` still owns which columns appear and in what order; a `column` block is a formatting overlay keyed by name, not a second column list:',
      { code: `table results {
  query = "SELECT NoNota, TglJual, Qty, Total FROM tr_jual ORDER BY TglJual DESC"
  value = NoNota
  show  = [NoNota, TglJual, Qty, Total]
  column Qty   { align = center }
  column Total { align = right; format = "#,##0" }   # align: left|right|center · format: number/date mask
}` },
    ],
  },
  {
    id: 'table-dropdown',
    group: 'Widgets',
    title: 'table dropdown — searchable multi-column picker',
    keywords: 'dropdown select2 multi column searchable collapsed popover table picker combo display',
    body: [
      'Add `dropdown = true` to a `table` and it becomes a Select2-style control: a collapsed button showing the selected value; clicking opens a popover with a search box and the rows as a multi-column table. Pick a row → it collapses again.',
      { code: `table cust {
  label    = "Pelanggan"
  query    = "SELECT KodeCust, NamaCust, Kota FROM m_customer ORDER BY NamaCust"
  value    = KodeCust            # bound value (what :cust / {cust} resolve to)
  display  = NamaCust            # column shown in the collapsed control after picking
  show     = [KodeCust, NamaCust, Kota]   # columns in the dropdown table
  search   = [NamaCust, KodeCust]         # which columns the search box filters
  dropdown = true
}
label sel { label = "{cust} — picked above" }` },
      'Use it when a plain `select` is too narrow to disambiguate rows (you need to see code + name + city at once).',
    ],
  },
  {
    id: 'labels',
    group: 'Widgets',
    title: 'label & note — display text',
    keywords: 'label note text display interpolation message static read-only caption bold italic size font heading emphasis',
    body: [
      'Non-interactive text. `label` and `note` both render a line of text with `{interp}` support — handy for live status from a `let` or a picked value.',
      { code: `let info = if(ada > 0, concat("Nota ", q, " ADA"), concat("Nota ", q, " tidak ada"))
label cek { label = "{info}" }` },
      'Anything in `{…}` is resolved live: a scalar widget’s value, a table/select’s value column, a `let`, or a nested field like `{cust.Kota}`.',
      '## Formatting',
      'Both widgets take `bold` / `italic` (booleans) and `size` — a keyword (`sm` | `md` | `lg` | `xl`) or any raw CSS size (`"20px"`, `"1.4em"`). Handy for a section heading or an emphasized warning:',
      { code: `label judul     { label = "Data Pelanggan"; bold = true; size = "lg" }
note  catatan   { label = "Semua field wajib diisi."; italic = true; size = "sm" }
label peringatan { label = "PERHATIAN"; bold = true; italic = true }` },
    ],
  },
  {
    id: 'conn-db',
    group: 'Widgets',
    title: 'connection / database pickers',
    keywords: 'connection database picker server choose runtime where runs',
    body: [
      'Let the user choose where the form runs. `connection` lists visible connections; `database` lists databases on the chosen connection.',
      { code: `connection conn { label = "Server" }
database   db   { label = "Database" }` },
      'These are system-backed widgets — they take no `query`/`options` (the validator rejects those). For a friendlier single-pick alias, use a `target` instead.',
    ],
  },
  {
    id: 'target',
    group: 'Widgets',
    title: 'target — branch (preset conn+db)',
    keywords: 'target branch cabang preset connection database routing alias multi-branch',
    body: [
      'One dropdown of friendly aliases, each routing the form to a preset (connection, database) pair. Picking one switches where every query/action runs.',
      { code: `target cabang {
  label = "Cabang"
  options = [
    { label = "Cabang Mojokerto", connection = "001 Bayiku Surodinawan", database = "dbBayiku" },
    { label = "Cabang Madiun",    connection = "002 Bayiku Madiun",      database = "dbBayiku" }
  ]   # keys: label, connection (name or id), database
}` },
      'In a saved form, the `target` options ARE the authorization: the server validates the picked target against them before routing. Until a target is picked, the form has no connection, so its queries wait. Changing the branch clears stale selections in dependent query widgets so a button can’t fire against the new connection with the old branch’s key.',
    ],
  },

  // ── Binding & reactivity ───────────────────────────────────────────────────
  {
    id: 'bind',
    group: 'Binding & reactivity',
    title: ':bind — values into SQL',
    keywords: 'bind colon sql parameter injection safe set var dotted row field',
    body: [
      'Inside any SQL string, `:name` injects a binding’s value as a bound parameter — safe from SQL injection. A bare `:name` uses the binding’s value column; `:row.Col` / `:cust.Alamat` picks a field of a record.',
      { code: `query = "SELECT * FROM tr_jual WHERE NoNota = :nota AND KodeCust = :cust.KodeCust"` },
      'A query re-runs automatically whenever any `:bind` it references changes — that’s how cascading (master → detail) works. Binds only resolve from widget values, so they never form a reactive cycle.',
    ],
  },
  {
    id: 'interp',
    group: 'Binding & reactivity',
    title: '{interp} — values into display strings',
    keywords: 'interp interpolation brace display label toast confirm dotted nested field',
    body: [
      'In a display string, `{name}` substitutes a value’s text. Supports nested fields: `{root.field}`.',
      { code: `toast   = "Hapus nota {nota} berhasil."
label   = "Tanggal: {results.TglJual} — kasir {results.Kasir}"
confirm = "Yakin hapus {nota}?"` },
      '`{name}` → that binding’s scalar text: a scalar widget’s value, a table/select’s value column, or a `let`. `{user.name}` gives the current user. A missing or `null` value renders as empty (so a label is safe before a pick). Values are shown **as-is — no `format` is applied** — so format in SQL (`FORMAT(x, 0)`, `DATE_FORMAT(d, "%d/%m/%Y")`) if you need separators or a date pattern in the text.',
    ],
  },
  {
    id: 'row-vs-value',
    group: 'Binding & reactivity',
    title: 'Value column vs row object (the sharp edge)',
    keywords: 'value column row object expression difference dot column sharp edge gotcha',
    body: [
      'The same table/select name resolves differently by context — the single most important rule to internalize:',
      '- In a **SQL `:bind`** and a **`{interp}`**: a bare name = its **value column** (a scalar).',
      '- In an **expression** (`enabled`, `visible`, `with{}`, `computed`, `default`): a bare name = its **row object** — so you must add `.Column`.',
      { code: `# table cust { value = KodeCust  …  }
enabled = cust is set            # expression: cust is the row → truthiness ok
default = cust.Alamat            # expression: must say .Alamat (a field of the row)
query   = "… WHERE Kode = :cust" # SQL bind: :cust is the value column (KodeCust)
label   = "Picked {cust}"        # interp: {cust} is the value column (KodeCust)` },
      'Mnemonic: **`{}` and `:` give the value column; an expression gives the row — add `.Column`.**',
    ],
  },
  {
    id: 'reactivity',
    group: 'Binding & reactivity',
    title: 'Reactivity & recompute',
    keywords: 'reactive recompute visible enabled cascade dependency cycle reset dependents',
    body: [
      'Everything is reactive. When a value changes, the engine recomputes — in dependency order — every expression and query that referenced it: `visible`, `enabled`, `default`, `computed`, dependent queries, and `let`s.',
      '## Two consequences',
      '- A hidden widget (`visible = false`) is excluded from binds — its value won’t leak into SQL.',
      '- Resetting/clearing a binding clears its transitive dependents (e.g. switching a `target` clears dependent query selections).',
      '- A `select`/`table` clears its **own** selection when a re-query (typically a new search term) drops the picked row out of the result set — so a dependent detail grid never references a row that’s no longer visible. (When the picked row is still in the filtered list, the selection is kept.)',
      '## You don’t sequence it',
      'You reference values; the graph propagates. Example chain, all automatic: pick a master `select` → the detail `table`’s `:master` bind changes → the table re-queries → a `let` summing the table recomputes → a `label`/`enabled` that reads the `let` updates. Add a value, and everything downstream catches up in dependency order.',
      'The dependency graph must be acyclic (`E_CYCLE`) — A’s expression can’t depend on B while B’s depends on A.',
    ],
  },
  {
    id: 'search-fn',
    group: 'Binding & reactivity',
    title: 'search() — multi-column filter in SQL',
    keywords: 'search function like filter multi word column query box contains',
    body: [
      'A SQL-only builtin that expands to a multi-word, multi-column OR-of-LIKE — the easy way to wire a search box to a list.',
      { code: `text q { label = "Cari" }
table rows {
  query = "SELECT id, nama, kode FROM barang WHERE search(:q, nama, kode) ORDER BY nama"
  value = id
  show  = [nama, kode]
}` },
      '`search(:q, nama, kode)` matches each word of `q` against any of the listed columns (AND across words, OR across columns). An empty `:q` matches everything.',
    ],
  },

  // ── Expressions ────────────────────────────────────────────────────────────
  {
    id: 'operators',
    group: 'Expressions',
    title: 'Operators & comparisons',
    keywords: 'operator comparison and or not is set empty in arithmetic equals expression boolean',
    body: [
      'Expressions power `visible`, `enabled`, `default`, `computed`, `require`, and `if/when` conditions.',
      '- Comparison: `== != < > <= >=`',
      '- Logical: `and`, `or`, `not`',
      '- Presence: `x is set`, `x is empty`',
      '- Membership: `x in ["A", "B"]`',
      '- Arithmetic: `+ - * /` (and unary `-`)',
      { code: `enabled = nota is set and qty > 0
visible = jenis == "Kredit"
enabled = status in ["baru", "revisi"]
computed = qty * price` },
    ],
  },
  {
    id: 'builtins',
    group: 'Expressions',
    title: 'Builtins — if, concat, dates, aggregates',
    keywords: 'builtin if concat today now sum count avg min max aggregate function ternary',
    body: [
      'Functions usable in any expression:',
      '- `if(cond, a, b)` — ternary.',
      '- `concat(…)` — join values into a string.',
      '- `today()`, `now()` — current date / timestamp.',
      '- `sum/count/avg/min/max(table.Col)` — aggregate a table/grid column.',
      { code: `let label  = if(qty > 0, concat("Qty: ", qty), "kosong")
let grand  = sum(lines.rows.total)
date tgl   { default = today() }` },
      'For an aggregate over a grid use its `.rows` accessor: `sum(items.rows.total)`.',
    ],
  },
  {
    id: 'let',
    group: 'Expressions',
    title: 'let — derived values (sync & async)',
    keywords: 'let derived value sync async sql scalar row rows query reactive compute',
    body: [
      'A `let` derives a reusable value. Two kinds:',
      '## Synchronous',
      'Computed from other bindings — arithmetic, `concat`, `if`, field access. Recomputes reactively.',
      { code: `let nomor = results.NoJualR
let grand = sum(lines.rows.total)` },
      '## Asynchronous query',
      '`sql.scalar/row/rows("…")` runs a query reactively, re-running when its `:binds` change. `scalar` → first cell, `row` → a record, `rows` → a table.',
      { code: `text q { label = "No Jual" }
let ada  = sql.scalar("SELECT COUNT(*) FROM tr_jualr_m WHERE NoJualR = :q")
let info = if(ada > 0, concat("Nota ", q, " ADA"), concat("Nota ", q, " tidak ada"))
label cek { label = "{info}" }` },
      'Shapes: `sql.scalar` → the first cell (ideal for counts/flags); `sql.row` → a record you read fields off (`let cust = sql.row("SELECT * FROM m_customer WHERE Kode = :pick")` then `{cust.Nama}`); `sql.rows` → a list to aggregate (`sum`/`count`/…). While a query is in flight the value is `null`, so guard with `if(ada > 0, …)` or `ada is set` rather than assuming a number.',
      'A `let` is usable everywhere — `{interp}`, expressions, `enabled`, `with{}`, and `:binds` (so one query can feed another: `… WHERE id = :cust.Id`). A `let` cannot be referenced before it’s declared and the dependency graph must stay acyclic. (A widget `default = sql.scalar(…)` is sync-only and runs once; for a value that re-queries, use a `let`.)',
    ],
  },
  {
    id: 'user',
    group: 'Expressions',
    title: 'user — the current user',
    keywords: 'user id name role current login context auto vNinocatUser',
    body: [
      'A read-only context object available in any expression: `user.id`, `user.name`, `user.role`.',
      { code: `label who { label = "Login: {user.name} ({user.role})" }
enabled = user.role == "admin"` },
      'For stored procedures, the runtime auto-binds `@vNinocatUser = user.name` on every CALL — you never need to pass it in a `with { }` block.',
    ],
  },

  // ── Buttons & actions ──────────────────────────────────────────────────────
  {
    id: 'button',
    group: 'Buttons & actions',
    title: 'button — confirm, enabled, role, run',
    keywords: 'button action run confirm enabled role validate click submit',
    body: [
      'A button gathers a `run { }` block of effects, plus optional guards.',
      { code: `button "Hapus" {
  enabled  = item is set          # reactive — disabled until true
  role     = admin                # admin | subadmin | user (or [a, b])
  confirm  = "Hapus {item}?"       # blocking yes/cancel before running
  validate = true                 # run the form-level validate{} first (default true)
  run {
    call "sp_delete(:item)"
    toast "Terhapus."             # the form re-runs its queries automatically after a write
  }
}` },
      '`enabled`, `visible`, `confirm`, and the `validate` toggle are wired. `confirm` shows a blocking yes/cancel (with `{interp}`) before anything runs; set `validate = false` to skip the form-level `validate { }` for this button.',
      '`role = admin | [admin, subadmin]` shows the button only to a matching role — other users don’t see it. That’s a client-side UX guard; a saved form’s overall access is governed server-side by form grants (Forms tab), so keep security-critical checks in the SP or a `validate { }` SQL `require`.',
    ],
  },
  {
    id: 'run-steps',
    group: 'Buttons & actions',
    title: 'run steps — call/exec/toast/close/refresh',
    keywords: 'run step call exec toast info warn error alert close refresh open effect',
    body: [
      'Inside `run { }` (and grid hooks / `save`), the available effects:',
      '- `call "sp(:a)"` — call a stored procedure (auto-prefixes `CALL`).',
      '- `exec "<raw SQL>"` — run raw non-CALL SQL.',
      '- `require <expr | "SQL"> else "…"` — veto the action (an expression aborts on the client; a SQL check guards inside the action’s transaction).',
      '- `toast "…"` / `info` / `warn` / `error` — toasts. `alert "…"` — a must-acknowledge dialog.',
      '- `close()` — close the form.',
      '- `refresh <widget>` — accepted, but a successful action **already re-runs every query in the form automatically**, so an explicit `refresh` is usually redundant (per-widget targeting isn’t distinct yet).',
      '- `open "Form" with { … }` — navigate to another saved form, seeding its `param`s from the with{} values. (No-op in the workbench preview, which has nowhere to navigate.)',
      { code: `button "Cek" {
  run {
    toast "Berhasil."
    info  "Sekadar info."
    warn  "Perlu perhatian."
    error "Ada masalah."
    alert "Harus diklik OK."
  }
}` },
      'Order of execution: the mutating steps (`call` / `exec` / `require`, plus any `validate { }` SQL guards) are sent to the server as **one batched action** so they share a transaction; if it succeeds, the form re-runs its queries, then the client effects (`toast` / `info` / `warn` / `error` / `alert` / `close`) fire in document order. If the action is aborted (a `require` fails), the client effects do not run.',
      'Effects are legal only inside a `run` block or a grid before/after hook (`E_EFFECT_PLACEMENT` otherwise). A `for x in <array> { … }` loop runs its body once per item — the array is anything in scope (a `sql.rows` let or a grid accessor like `g.rows`), and inside the body `:x.<col>` / `{x.<col>}` bind that item; the per-item server steps all run in the one action transaction.',
    ],
  },
  {
    id: 'call-with',
    group: 'Buttons & actions',
    title: 'call auto-CALL + with { } session vars',
    keywords: 'call CALL auto wrap with session var vName stored procedure exec',
    body: [
      '`call` auto-prefixes `CALL`: `call "sp(:a)"` runs `CALL sp(@va)`. A statement already starting with `CALL`/`INSERT`/`UPDATE`/`DELETE`/`REPLACE`/`SET`/`SELECT`/`DO` is used verbatim. For raw non-CALL SQL, use `exec`.',
      'The `with { Name = expr }` block SETs `@vName` before the call — the classic pattern for procs that read free-standing `@v…` session vars and take no arguments:',
      { code: `button "Simpan" {
  enabled = kodeBrg != ""
  run {
    call "sp_insertbarang()" with {
      KodeBrg = kodeBrg,   # → SET @vKodeBrg
      NamaBrg = namaBrg,   # → SET @vNamaBrg
    }
    toast "Tersimpan."
    close()
  }
}` },
      '`@vNinocatUser` (the logged-in user) is set automatically — don’t list it.',
    ],
  },
  {
    id: 'branching',
    group: 'Buttons & actions',
    title: 'if / when branching in run',
    keywords: 'if else when branch conditional flow run step require',
    body: [
      'Inside a `run` block, branch on the live scope. `if/else` is two-way; `when` is one-way (runs the block only when true).',
      { code: `button "Proses" {
  run {
    require qty > 0 else "Qty harus lebih dari 0."
    if status == "baru" {
      call "sp_proses_baru(:id)"
    } else {
      call "sp_proses_lama(:id)"
    }
    when qty > 100 { warn "Qty besar — mohon diperiksa." }
    toast "Selesai."
  }
}` },
    ],
  },

  // ── Grids ──────────────────────────────────────────────────────────────────
  {
    id: 'grid-basics',
    group: 'Grids',
    title: 'grid — editable rows',
    keywords: 'grid editable add remove key rows insert update delete master detail',
    body: [
      'A `grid` is an editable table: load rows with a query, let the user add/edit/remove, then persist the diff via SPs. `key` (the row-identity column) is required.',
      { code: `select header { label = "Header"; query = "SELECT id, code FROM header_table ORDER BY code"; value = id }
grid items {
  label  = "Items"
  query  = "SELECT id, code, qty FROM detail_table WHERE header_id = :header ORDER BY id"
  key    = id          # row identity (required)
  add    = true        # allow inserting rows
  remove = true        # allow deleting rows
  save   = "Simpan"    # Save button caption (default "💾 Save")
  insert = "sp_item_insert(:header, :row.code, :row.qty)"   # :row.<col> = the row's cell
  update = "sp_item_update(:row.id, :row.qty)"
  delete = "sp_item_delete(:row.id)"
  column code { edit = insert }            # edit: always (default) | insert | never
  column qty  { type = number; min = 0; required = true }
}` },
      'On **Save**, the engine diffs the working rows against what was loaded and runs, in one transaction: `insert` for each added row, `update` for each edited row (any cell other than the key changed), `delete` for each removed row. `:row.<col>` binds that row’s current cell and, on an `update`, `:row.old.<col>` binds the value before editing — e.g. an optimistic check `… WHERE qty = :row.old.qty`. A non-row bind like `:header` resolves from the form. Unchanged rows are skipped. A grid requires `key` (`E_MISSING_KEY` otherwise) and at least one of `insert` / `update` / `delete`. Per-row order: before-hook → persist step → after-hook, all in the same transaction.',
      '## The Save button',
      'It appears whenever the grid persists. Set its caption with `save = "…"` (default `💾 Save`), and it stays **disabled until there are unsaved changes**, so you can’t fire an empty save. After a successful save it re-disables until you change something again.',
      '## Clearing on a master change',
      'When the master record the grid depends on changes — you pick a different parent row, **or a refilter drops the selected one out of view so it deselects** — the grid is **cleared, including rows you marked for deletion before saving**. This prevents acting on a stale or no-longer-visible parent. (The grid resets the moment the master `:bind` changes, before its re-query even returns.)',
    ],
  },
  {
    id: 'grid-column-types',
    group: 'Grids',
    title: 'Column input types (enum dropdown, number, date, check)',
    keywords: 'column type select enum dropdown options number date check constrain input grid editable required',
    body: [
      'A grid column’s editor is chosen by its `type`. The default is a text box; `type = select` turns the cell into a **constrained dropdown**, so a user can only enter values from your list.',
      '## Enum dropdown',
      'Add `type = select` and `options`. The **stored** value is the option’s `value` (what `:row.<col>` sends to your SP); the user sees the `label`. Both option forms work — objects (value ≠ label) or bare strings (value = label). A blank entry is always offered; add `required = true` to make a choice mandatory.',
      { code: `grid items {
  query  = "SELECT id, kode, satuan, status FROM faktur_item WHERE faktur_id = :header ORDER BY id"
  key    = id
  add    = true
  insert = "sp_item_insert(:header, :row.kode, :row.satuan, :row.status)"
  update = "sp_item_update(:row.id, :row.satuan, :row.status)"

  column kode   { edit = insert }                  # set on insert, locked after
  column status {                                  # stored = A/N, shown = Aktif/Nonaktif
    type    = select
    options = [{ value = "A", label = "Aktif" }, { value = "N", label = "Nonaktif" }]
    required = true
  }
  column satuan { type = select; options = ["pcs", "box", "lusin"] }   # bare strings: value = label
}` },
      '## Other column types',
      '- `type = number` — a numeric input (honors `min` / `max`).',
      '- `type = date` — a date picker.',
      '- `type = check` — a checkbox (boolean).',
      '- *(no type)* — a plain text box.',
      'These compose with the read-only options: e.g. `edit = insert` lets the dropdown be chosen on new rows but locks it once the row exists. `required = true` is enforced at save (a blank required cell blocks it, client-side); keep security-critical checks in the SP too.',
      'For choices that come from a **table** (FK-style) rather than a fixed list, use `lookup`: `column kode { lookup = "SELECT KodeBrg, NamaBrg FROM m_barang ORDER BY NamaBrg" }` renders a dropdown of those rows — value = the column’s `value` or the 1st result column, label = `map` / `display` or the 2nd column. Read-only cells show the matching label. The options load once per column (and re-run if the lookup query has `:binds` that change).',
    ],
  },
  {
    id: 'grid-columns',
    group: 'Grids',
    title: 'Computed columns + footer totals',
    keywords: 'column computed footer sum total format readonly aggregate grid',
    body: [
      'A `computed` column is read-only and derived per row; `footer = sum` shows a column total.',
      { code: `grid lines {
  label = "Lines"
  query = "SELECT id, qty, price FROM lines WHERE doc_id = :doc ORDER BY id"
  key   = id
  column qty   { type = number; align = right }
  column price { type = number; align = right }
  column total { computed = qty * price; format = "#,##0"; footer = sum; align = right; readonly = true }
}
let grand = sum(lines.rows.total)   # aggregate the grid: sum | count | avg | min | max` },
      'Column properties: `edit` (always | insert | never), `type`, `computed`, `footer = sum`, `format`, `align`, `lookup` (+ `map`), and the `required` / `min` / `max` / `maxlen` / `pattern` constraints — all wired. `align = left | right | center` sets the cell text-alignment — ideal for money/integer columns — and applies to the header, the cells (read-only and editable), and the footer sum. `format` applies to computed cells, read-only cells, and footer sums (`format = "#,##0"` → `1,234,567`); constraints are checked per added/edited row at save; `lookup = "SELECT val, label FROM …"` makes the cell a from-a-table dropdown. (`from` is import-only.)',
    ],
  },
  {
    id: 'grid-hooks',
    group: 'Grids',
    title: 'Grid data hooks',
    keywords: 'grid hook before after insert update delete require exec recalc per row',
    body: [
      '`before`/`after` `insert`|`update`|`delete` hooks run around each row at save time — for guards and recalculations.',
      { code: `grid items {
  query  = "SELECT id, qty FROM detail_table WHERE doc_id = :doc ORDER BY id"
  key    = id
  insert = "sp_item_insert(:doc, :row.qty)"
  delete = "sp_item_delete(:row.id)"
  before delete { require "SELECT 1 FROM doc WHERE id = :doc AND posted = 0" else "Sudah diposting." }
  after  insert { exec "CALL sp_recalc(:doc)" }
  after  delete { exec "CALL sp_recalc(:doc)" }
}` },
    ],
  },

  // ── Validation ─────────────────────────────────────────────────────────────
  {
    id: 'validate',
    group: 'Validation',
    title: 'validate { } — cross-field rules',
    keywords: 'validate require else rule cross field guard sql expression veto',
    body: [
      'A form-level `validate { }` runs before any mutating button (opt out per button with `validate = false`). Each `require` is an expression OR a SQL check, with a mandatory `else` message.',
      { code: `validate {
  require qty > 0                                    else "Qty harus lebih dari 0."
  require tglAkhir >= tglAwal                        else "Tanggal akhir < awal."
  require "SELECT 1 FROM ms_cust WHERE Kode = :cust" else "Customer tidak valid."
}` },
      'Expression `require`s are checked on the client and abort immediately on failure. SQL `require`s are prepended as guards inside the action’s transaction (so they veto a write atomically — the `else` text becomes the error). Every `require` needs an `else "…"` (`E_REQUIRE_ELSE`).',
      'Widget-level `required` / `min` / `max` are enforced client-side now, but a saved form’s server run doesn’t re-check them — so for a rule that must be **unbypassable**, put it here as a SQL `require` (it runs in the action’s transaction) or in the SP. A SQL `require` is also how you check things the client can’t see — existence, uniqueness, current row state.',
    ],
  },

  // ── Routing & targets ──────────────────────────────────────────────────────
  {
    id: 'where-runs',
    group: 'Routing & targets',
    title: 'Where a form runs',
    keywords: 'connection database meta pin picker target routing where runs choose',
    body: [
      'Four ways to decide the connection + database, in order of precedence:',
      '- A `target` pick (branch alias) — overrides everything once chosen.',
      '- `connection`/`database` widget picks.',
      '- `meta { connection = …; database = … }` pins.',
      '- The workbench toolbar connection (preview), or the saved form’s stored default (run-by-id).',
      'For end-user forms that must run against one of several branches, prefer a `target` — its options double as the server-side authorization list.',
    ],
  },
  {
    id: 'run-modes',
    group: 'Routing & targets',
    title: 'Workbench preview vs saved run',
    keywords: 'workbench preview run-by-id saved server security raw sql whitelist admin',
    body: [
      'The same script runs two ways:',
      '- **Workbench preview** (admin): the client sends the analyzed plan’s SQL to the connection you pick. For building + testing.',
      '- **Saved run (run-by-id)**: an end-user runs a stored form from the Forms tab. The server drives the stored script and only its whitelisted SQL — the client sends the chosen node/steps + values + the picked target, never raw SQL.',
      'Design once; the engine enforces the security boundary on the saved path. A `target`’s options are the authorization — the server validates the picked (connection, database) against them.',
    ],
  },

  // ── Reference ──────────────────────────────────────────────────────────────
  {
    id: 'enforced',
    group: 'Reference',
    title: 'What the engine enforces (vs. accepted-only)',
    keywords: 'enforced implemented not yet supported required min max format role open lookup map for declarative validate limitation gotcha caveat parses',
    body: [
      'Most properties are enforced by the runtime now; a few still only parse (listed under *Not wired yet*). One nuance: constraint checks (`required`/`min`/`max`/…) and `role` run **client-side** — they’re real UX guards, but a saved form’s server run only whitelists SQL, it doesn’t re-check them. **For a rule that must not be bypassable, also enforce it in a `validate { }` SQL `require` or the stored procedure.**',
      '## Wired — the runtime honors these',
      '- Reactivity: `visible`, `enabled`, `default` (a `sql.scalar` default evaluates once), grid `computed`.',
      '- Inputs: `readonly`; `type` selects the editor (text / number / date / check, and on a grid column `type = select` → a dropdown).',
      '- Constraints: `required`, `min` / `max` (number), `maxlen` / `pattern` (text) — enforced before a mutating action (and shown as live input hints); grid columns are checked per added/edited row at save.',
      '- `format` — number/date masks (`#,##0`, `0.00`, `dd/MM/yyyy`, …) applied to grid computed cells, read-only cells, footer sums, and read-only scalar fields. (Editable inputs and `{interp}` stay raw — format those in SQL.)',
      '- Button `role` — a button is shown only to a matching role (`role = admin | [admin, subadmin]`); other users don’t see it.',
      '- Data: `query`, `value`, `display`, `show`, `options`, `search` (`= true | [cols] | { columns, mode, min }`), table `dropdown`.',
      '- Grid: `key`, `add`, `remove`, `save` (Save caption; the button auto-disables when there are no unsaved changes), `insert` / `update` / `delete`, `:row.<col>` (current cell) + `:row.old.<col>` (pre-edit value on an update), column `align` (left/right/center), `footer = sum`, before/after hooks. Changing the master `:bind` the grid depends on (or it deselecting) clears the grid, including rows marked for deletion.',
      '- Table: a `column X { align, format }` overlay — same `align` / `format` as a grid column, applied to that column’s header + cells (`show` still decides which columns appear and their order).',
      '- Layout: `spacer` (`direction` + `size`) — a fixed vertical or horizontal gap. Label/note `bold`, `italic`, `size` (`sm`|`md`|`lg`|`xl` or a raw CSS size).',
      '- Actions: `call` (auto-CALL), `exec`, `require`, `with { }`, `if` / `when`, `toast` / `info` / `warn` / `error` / `alert`, `close()`, button `confirm`, the form-level `validate { }`.',
      '- Form: `meta` (title / width / connection / database), `param` (seeded from the opening context + its default), `connection` / `database` / `target`.',
      '- Navigation: `open "Form" with { … }` — opens another saved form, seeding its `param`s from the with{} values (a no-op in the workbench preview).',
      '- Loops: `for x in <array> { … }` — iterates an array in scope (a `sql.rows` let, or a grid accessor) binding `x` per item; `:x.<col>` / `{x.<col>}` resolve.',
      '- Grid scope: a grid publishes `g.rows` (incl. computed columns) and `g.added` / `g.edited` / `g.removed` into the form scope, so `sum(g.rows.total)`, `count(g.added)`, and `for r in g.rows` all work.',
      '- Column `lookup` — a grid column dropdown populated from a query (`lookup = "SELECT val, label FROM …"`; value = the column’s `value` or the 1st result column, label = `map` / `display` / the 2nd column).',
      '## Not wired yet',
      '- `load` / `set` run-block statements — use a `let` for a derived value and `with { }` for session vars instead.',
      '- Column `from` is import-only (it maps a file header in the Import DSL).',
      'When in doubt, test it in the live preview.',
    ],
  },
  {
    id: 'prop-reference',
    group: 'Reference',
    title: 'Property reference by node',
    keywords: 'reference properties widget button grid column meta options list all props',
    body: [
      'Known properties per node (an unknown one is `E_UNKNOWN_PROP`). Being *known* means it parses — not that the runtime acts on it; see *What the engine enforces* for which are wired today.',
      '## Widgets (text/number/date/check/select/table)',
      '`label`, `visible`, `enabled`, `default`, `required`, `readonly`, `format`, `type`, `min`, `max`, `pattern`, `maxlen`, `value`, `display`, `query`, `options`, `show`, `search`. Table-only: `dropdown` (Select2 picker), `display` (collapsed column), `column` blocks (display overlay — see below). Label/note-only: `bold`, `italic`, `size`.',
      '## spacer',
      '`direction` (`vertical` default | `horizontal`), `size` (a CSS length, default `1rem`). No `label`/name — it holds no value.',
      '## button',
      '`label`, `enabled`, `visible`, `confirm`, `role`, `run`, `validate`.',
      '## grid',
      '`label`, `query`, `key`, `add`, `remove`, `save`, `insert`, `update`, `delete`, plus `column` blocks and `before/after` hooks.',
      '## column (grid)',
      '`label`, `type`, `edit`, `computed`, `footer`, `align`, `from`, `lookup`, `map`, `required`, `min`, `max`, `format`.',
      '## column (table)',
      'Narrower — a table column is read-only display, not editable: `label`, `align`, `format` only.',
      '## meta',
      '`title`, `width`, `connection`, `database`, `locale`.',
      '## Roles',
      '`admin | subadmin | user` — `role = X` or `role = [X, Y]`.',
    ],
  },
  {
    id: 'builtins-reference',
    group: 'Reference',
    title: 'Builtins & context reference',
    keywords: 'reference builtin function user grid accessor rows added edited removed sql scalar',
    body: [
      '## Context object `user`',
      '`user.id`, `user.name`, `user.role` (read-only). Auto-binds `@vNinocatUser = user.name` for CALLs.',
      '## Grid accessors (for a grid `g`)',
      '`g.rows` (incl. computed columns), `g.added`, `g.edited`, `g.removed` are published into scope as table-typed values — aggregate them (`sum(g.rows.total)`, `count(g.added)`) or iterate them in a run block (`for r in g.rows { … }`). Inside a grid persist step or such a loop, `:row.<col>` / `:r.<col>` binds the current cell and `:row.old.<col>` the pre-edit value.',
      '## Callable builtins',
      '`sql.scalar/row/rows("…")`, `if(c, a, b)`, `concat(…)`, `today()`, `now()`, `sum/count/avg/min/max(table.Col)`. SQL-only: `search(:t, …)`.',
    ],
  },
  {
    id: 'errors',
    group: 'Reference',
    title: 'Error codes',
    keywords: 'error code diagnostic E_SIGIL_CTX E_CYCLE E_ENUM_SOURCE validation troubleshoot',
    body: [
      'Diagnostics from the analyzer (shown in the preview pane):',
      '- `E_SIGIL_CTX` — `:bind` in a non-SQL prop, or `{interp}` in a SQL prop.',
      '- `E_EFFECT_PLACEMENT` — an effect (call/exec/toast/…) outside `run`/hooks/`save`.',
      '- `E_DUP_NAME` — two bindings share a name in one scope.',
      '- `E_CYCLE` — the dependency graph has a cycle.',
      '- `E_MISSING_KEY` — a `grid` lacks `key`, or a save lacks insert/update/delete.',
      '- `E_ENUM_SOURCE` — a `select` has both or neither of `options`/`query`.',
      '- `E_REQUIRE_ELSE` — a `require` without an `else "…"`.',
      '- `E_RECORD_NO_FIELD` — a multi-column record used in SQL/expr without a field.',
      '- `E_UNKNOWN_WIDGET` / `E_UNKNOWN_PROP` — unknown widget type / property.',
      '- `E_TYPE` — operand/property type mismatch (e.g. `min` on a non-number).',
      '- `E_PARSE` — a lexical/syntactic error.',
      '- `E_PARAM_REQUIRED` (runtime) — a required `param` wasn’t supplied at open.',
    ],
  },

  // ── Recipes ────────────────────────────────────────────────────────────────
  {
    id: 'recipe-delete',
    group: 'Recipes',
    title: 'Recipe — delete a record by code',
    keywords: 'recipe delete exists check confirm sp button example hapus nota',
    body: [
      'A search box that checks whether a code exists (live), enables a confirm-delete button only when it does, and calls a delete SP.',
      { code: `meta { title = "Hapus Penjualan per Nota" width = "sm" }

text q { label = "No Nota" }

# live existence check — re-runs as you type
let ada  = sql.scalar("SELECT COUNT(*) FROM tr_jualr_m WHERE NoJualR = :q")
let info = if(ada > 0, concat("Nota ", q, " ditemukan"), concat("Nota ", q, " tidak ada"))
label cek { label = "{info}" }

button "Hapus" {
  enabled = ada > 0
  confirm = "Hapus nota {q}?"
  run {
    call "sp_hapuspenjualan_pernota()" with { NoNota = q }
    toast "Hapus nota {q} berhasil."
    close()
  }
}` },
    ],
  },
  {
    id: 'recipe-master-detail',
    group: 'Recipes',
    title: 'Recipe — master → detail table',
    keywords: 'recipe master detail cascade select table cascading example',
    body: [
      'Pick a master row; a detail table re-queries for it automatically via `:master`.',
      { code: `select master {
  label = "Order"
  query = "SELECT id, code FROM orders ORDER BY code DESC"
  value = id
  display = code
  search = true
}
table detail {
  label = "Lines"
  query = "SELECT id, line, qty, price FROM order_lines WHERE order_id = :master ORDER BY line"
  value = id
  show  = [line, qty, price]
}
label total { label = "Lines for order {master}" }` },
    ],
  },
  {
    id: 'recipe-grid',
    group: 'Recipes',
    title: 'Recipe — editable master-detail grid',
    keywords: 'recipe grid editable master detail save sp insert update delete total footer',
    body: [
      'A header pick drives an editable grid with a computed total and SP-backed save.',
      { code: `select header {
  label = "Faktur"
  query = "SELECT id, no_faktur FROM faktur ORDER BY id DESC"
  value = id
  display = no_faktur
}
grid items {
  label  = "Item"
  query  = "SELECT id, kode, qty, harga FROM faktur_item WHERE faktur_id = :header ORDER BY id"
  key    = id
  add    = true
  remove = true
  insert = "sp_item_insert(:header, :row.kode, :row.qty, :row.harga)"
  update = "sp_item_update(:row.id, :row.qty, :row.harga)"
  delete = "sp_item_delete(:row.id)"
  column kode  { edit = insert }
  column qty   { type = number; min = 0; required = true }
  column harga { type = number; min = 0 }
  column total { computed = qty * harga; format = "#,##0"; footer = sum; readonly = true }
}
label grand { label = "Total: {gt}" }
let gt = sum(items.rows.total)` },
    ],
  },
  {
    id: 'recipe-dropdown',
    group: 'Recipes',
    title: 'Recipe — searchable multi-column dropdown',
    keywords: 'recipe dropdown select2 multi column searchable picker customer example',
    body: [
      'A compact customer picker that shows code + name + city, searchable, collapsing to the chosen name.',
      { code: `table cust {
  label    = "Pelanggan"
  query    = "SELECT KodeCust, NamaCust, Kota FROM m_customer ORDER BY NamaCust"
  value    = KodeCust
  display  = NamaCust
  show     = [KodeCust, NamaCust, Kota]
  search   = [NamaCust, KodeCust, Kota]
  dropdown = true
}
text alamat { label = "Alamat"; default = cust.Alamat; visible = cust is set }
button "Pakai" {
  enabled = cust is set
  run { toast "Dipilih: {cust}"; close() }
}` },
    ],
  },
  {
    id: 'recipe-branch',
    group: 'Recipes',
    title: 'Recipe — branch-aware form (target)',
    keywords: 'recipe target branch cabang multi connection database switch example routing',
    body: [
      'One form that runs against any branch the user picks — the `target` switches the connection + database, and the list re-queries against it.',
      { code: `meta { title = "Penjualan per Cabang" width = "md" }

target cabang {
  label = "Cabang"
  options = [
    { label = "Surodinawan", connection = "001 Bayiku Surodinawan", database = "dbBayiku" },
    { label = "Madiun",      connection = "002 Bayiku Madiun",      database = "dbBayiku" }
  ]
}

text q { label = "Cari nota" }
table jual {
  label = "Penjualan"
  query = "SELECT NoJualR, TglJual, Total FROM tr_jualr_m WHERE search(:q, NoJualR) ORDER BY TglJual DESC"
  value = NoJualR
  show  = [NoJualR, TglJual, Total]
}
label info { label = "Cabang {cabang} — pilih nota di atas" }` },
      'Switching `cabang` re-points every query and clears the stale `jual` selection so an action can’t fire against the new branch with the old nota.',
    ],
  },
];
